import { DragDropModule } from "@angular/cdk/drag-drop";
import { CommonModule, JsonPipe, registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import localeNl from "@angular/common/locales/nl";
import { LOCALE_ID, enableProdMode, importProvidersFrom, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { DateAdapter, MAT_DATE_LOCALE, MatRippleModule, provideNativeDateAdapter } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatRadioModule } from "@angular/material/radio";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { ServiceWorkerModule } from "@angular/service-worker";
import { CurrencyMaskModule } from "ng2-currency-mask";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { AppRoutingModule } from "./app/app-routing.module";
import { CustomDateAdapter } from "./app/classes/custom-date-adapter";
import { CustomPaginator } from "./app/components/table/CustomPaginator";
import { BeforeSendInterceptor } from "./app/ffwd/interceptors/beforeSendInterceptor";
import { HasRoleGuard } from "./app/guards/hasRole";
import { ApiErrorInterceptor } from "./app/interceptors/api-error.interceptor";
import { LanguageModule } from "./app/modules/language.module";
import { LocalDatePipe } from "./app/pipes/date.pipe";
import { RootComponent } from "./app/screens/root/root";
import { ApiService } from "./app/services/api.service";
import { ApplicationService } from "./app/services/application.service";
import { AuthService } from "./app/services/auth.service";
import { CoachEnergyConsultService } from "./app/services/coach-energy-consult.service";
import { EnergyConsultService } from "./app/services/energy-consult.service";
import { SnackbarService } from "./app/services/snackbar.service";
import { ENVIRONMENT } from "./environments/environment";

if (ENVIRONMENT.PRODUCTION) {
  enableProdMode();
}

registerLocaleData(localeNl);

bootstrapApplication(RootComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      GoogleMapsModule,
      CurrencyMaskModule,
      CommonModule,
      BrowserModule,
      MatDatepickerModule,
      MatRadioModule,
      AppRoutingModule,
      FormsModule,
      ReactiveFormsModule,
      MatRippleModule,
      // TableVirtualScrollModule,
      LanguageModule,
      NgxMaterialTimepickerModule,
      DragDropModule,
      ServiceWorkerModule.register("ngsw-worker.js", {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: "registerWhenStable:30000",
      }),
    ),
    ApplicationService,
    ApiService,
    EnergyConsultService,
    HasRoleGuard,
    SnackbarService,
    CoachEnergyConsultService,
    LocalDatePipe,
    JsonPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BeforeSendInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomPaginator,
    },
    {
      provide: [MAT_DATE_LOCALE],
      useValue: "nl-NL",
    },
    {
      provide: [LOCALE_ID],
      useValue: "nl-NL",
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = initializeSession(inject(AuthService));
      return initializerFn();
    }),
    provideNativeDateAdapter(),
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
  ],
}).catch((err) => console.error(err));

export function initializeSession(auth: AuthService): () => Promise<void> {
  return async () => {
    await auth.initialize({ PORTAL_URL: ENVIRONMENT.PORTAL_URL ?? "", REDIRECT_URL: "/auth-callback", API_URL: ENVIRONMENT.API_URL });
  };
}
